/*
 * Copyright (c) Nexign, JSC, 2019-2023.
 */
/**
 * Table Link Block component
 *
 * We should use <a href... for click by middle button. Also don't forget about click with ctrl key.
 *
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @author Ilya Baranchikov
 * @author Evgeniy Naumov
 * @version 1.25.1
 * @since 1.0.0
 */

import { FC, Fragment, ReactNode, useCallback } from 'react';

import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { Dispatch } from 'redux';

import { logBusinessEvent } from '@/main/modules/logging';
import { ILogBusinessEventPayload } from '@/main/modules/logging/loggingTypes';
import {
    TableTextDefaultValueStyled,
    TableTextLinkStyled,
    TableTextTooltipStyled,
} from '@components/Table/components/TableColumns/styled/TableBlockStyled';
import getCellValue from '@components/Table/helpers/getCellValue';
import { openRoute } from '@routes/constants/externalRoutes';

interface ITableLinkBlockProps {
    children?: ReactNode;
    className?: string;
    defaultValue: string;
    isExternal?: boolean;
    logBusinessEventPayload?: ILogBusinessEventPayload;
    onClick?: (event: React.MouseEvent) => void;
    url?: string;
}

export const TableLinkBlock: FC<ITableLinkBlockProps & IMapDispatchToProps> = (props) => {
    const {
        children,
        defaultValue = '',
        isExternal = true,
        logBusinessEventPayload,
        url,
        onClick,
        logBusinessEvent,
    } = props;

    const value = getCellValue(children);

    const onClickHandler = (event: React.MouseEvent) => {
        event.preventDefault();

        logBusinessEventPayload && logBusinessEvent(logBusinessEventPayload);

        if (event.ctrlKey && url) {
            window.open(url, '_blank');
        } else if (onClick) {
            onClick(event);
        } else {
            window.open(url, '_self');
        }
    };

    const onLinkClickHandler = useCallback(
        (event: React.MouseEvent) => {
            event.preventDefault();

            logBusinessEventPayload && logBusinessEvent(logBusinessEventPayload);

            if (url) {
                openRoute(url);
            }
        },
        [logBusinessEvent, logBusinessEventPayload, url],
    );

    return (
        <Fragment>
            {value ? (
                <TableTextTooltipStyled>
                    <TableTextLinkStyled>
                        {url ? (
                            isExternal ? (
                                <a href={url} onClick={onClickHandler}>
                                    {value}
                                </a>
                            ) : (
                                <Link to={url} onClick={onLinkClickHandler}>
                                    {value}
                                </Link>
                            )
                        ) : (
                            value
                        )}
                    </TableTextLinkStyled>
                </TableTextTooltipStyled>
            ) : (
                <TableTextDefaultValueStyled>{defaultValue}</TableTextDefaultValueStyled>
            )}
        </Fragment>
    );
};

interface IMapDispatchToProps {
    logBusinessEvent: (payload: ILogBusinessEventPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IMapDispatchToProps => ({
    logBusinessEvent: (payload) => dispatch(logBusinessEvent(payload)),
});

export default connect(null, mapDispatchToProps)(TableLinkBlock);
